import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation , useOutletContext} from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { getImageUrl } from '../../utils/imageUtils.js';
import {
  ArrowLeft,
  Save,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle,
  Image as ImageIcon,
  Upload,
  X,
  FileText,
} from 'lucide-react';

import { createNews, updateNews, updateNewsStatus } from '../../utils/api/newsApi';

const content = {
  ne: {
    editTitle: 'लेख सम्पादन गर्नुहोस्',
    newTitle: 'नयाँ लेख लेख्नुहोस्',
    editSub: 'रिपोर्टरको लेख सम्पादन गरेर प्रकाशित गर्नुहोस्',
    newSub: 'नयाँ लेख लेखेर प्रकाशित गर्नुहोस्',
    titleLabel: 'शीर्षक',
    titlePlaceholder: 'लेखको शीर्षक लेख्नुहोस्...',
    sectorLabel: 'क्षेत्र',
    sectorPlaceholder: 'क्षेत्र छान्नुहोस्',
    contentLabel: 'सामग्री',
    contentPlaceholder: 'लेखको सामग्री यहाँ लेख्नुहोस्...',
    excerptLabel: 'संक्षिप्त विवरण',
    excerptPlaceholder: 'छोटो सारांश (वैकल्पिक)...',
    coverLabel: 'कभर इमेज',
    coverPlaceholder: 'https://...',
    uploadBtn: 'कम्प्युटरबाट अपलोड गर्नुहोस्',
    orUrl: 'वा तस्बिरको URL राख्नुहोस्',
    saveDraft: 'ड्राफ्ट सुरक्षित गर्नुहोस्',
    publish: 'प्रकाशित गर्नुहोस्',
    back: 'फर्कनुहोस्',
    required: 'यो फिल्ड आवश्यक छ',
    savedDraft: 'ड्राफ्ट सुरक्षित भयो',
    published: 'लेख प्रकाशित भयो!',
    reporterLabel: 'रिपोर्टर',
    editingBanner: 'तपाईं रिपोर्टरको लेख सम्पादन गर्दै हुनुहुन्छ',
    invalidImage: 'कृपया मान्य तस्बिर फाइल छान्नुहोस् (jpg, png, webp)',
    imageTooBig: 'तस्बिर 5MB भन्दा सानो हुनुपर्छ',
  },
  en: {
    editTitle: 'Edit Article',
    newTitle: 'Write New Article',
    editSub: "Edit the reporter's submission and publish it",
    newSub: 'Write and publish a new article directly',
    titleLabel: 'Title',
    titlePlaceholder: 'Enter article title...',
    sectorLabel: 'Sector',
    sectorPlaceholder: 'Select a sector',
    contentLabel: 'Content',
    contentPlaceholder: 'Write the article content here...',
    excerptLabel: 'Excerpt',
    excerptPlaceholder: 'Short summary (optional)...',
    coverLabel: 'Cover Image',
    coverPlaceholder: 'https://...',
    uploadBtn: 'Upload from computer',
    orUrl: 'or paste an image URL',
    saveDraft: 'Save Draft',
    publish: 'Publish',
    back: 'Back',
    required: 'This field is required',
    savedDraft: 'Draft saved',
    published: 'Article published!',
    reporterLabel: 'Reporter',
    editingBanner: "You're editing a reporter's submission",
    invalidImage: 'Please choose a valid image file (jpg, png, webp)',
    imageTooBig: 'Image must be smaller than 5MB',
  },
};

const SECTORS = [
  'शहरी योजना तथा भौतिक पूर्वाधार',
  'सुरक्षा तथा सार्वजनिक सुरक्षा',
  'खानेपानी तथा जल व्यवस्थापन',
  'फोहोर तथा प्रदूषण व्यवस्थापन',
  'वन, वातावरण तथा पर्यटन',
  'शिक्षा, अनुसन्धान तथा ज्ञान विकास',
  'शिल्प, सीप तथा उद्यमशीलता विकास',
  'स्वास्थ्य तथा जनस्वास्थ्य',
  'स्वस्थ जीवनशैली तथा उपभोक्ता हित संरक्षण',
  'खेलकुद तथा खेल प्रशिक्षण',
  'सूचना प्रविधि तथा स्मार्ट सिटी विकास',
  'मिडिया तथा सञ्चार',
  'व्यापार, उद्योग तथा आर्थिक विकास',
  'कृषि तथा सहकारी विकास',
  'विपद् व्यवस्थापन तथा जलवायु अनुकूलन',
  'कला, संस्कृति तथा पहिचान संरक्षण',
  'सुशासन, आर्थिक नीति तथा वित्तीय व्यवस्थापन',
  'सामाजिक सुरक्षा तथा समावेशी विकास',
  'महिला, बालबालिका तथा युवा विकास',
  'अन्तर्राष्ट्रिय सम्बन्ध तथा साझेदारी',
  'प्रशासन तथा संस्थागत व्यवस्थापन',
];

const Field = ({ label, error, children }) => (
  <div>
    <label className="mb-1.5 block text-xs font-extrabold text-char-500">{label}</label>
    {children}
    {error && <p className="mt-1 text-xs font-bold text-red-500">{error}</p>}
  </div>
);

const MAX_IMAGE_MB = 5;
const ACCEPTED_TYPES = ['image/jpeg', 'image/png', 'image/webp', 'image/jpg'];

const CreateArticle = () => {
  const {language = "ne"} = useOutletContext() || {};
  const t = content[language] || content.en;
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const fileInputRef = useRef(null);

  const existingArticle = location.state?.article || null;
  const isEditing = !!existingArticle;

  const [form, setForm] = useState({
    title: existingArticle?.title || '',
    sector: existingArticle?.sector || '',
    content: existingArticle?.content || '',
    excerpt: existingArticle?.excerpt || '',
    coverImage: existingArticle?.coverImage || existingArticle?.imageUrl || existingArticle?.image || '',
  });
 
  const [coverFile, setCoverFile] = useState(null);
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);

  const showToast = (type, message) => {
    setToast({ type, message });
    setTimeout(() => setToast(null), 3500);
  };

  const validate = () => {
    const e = {};
    if (!form.title.trim()) e.title = t.required;
    if (!form.sector) e.sector = t.required;
    if (!form.content.trim()) e.content = t.required;
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const set = (field) => (e) => {
    setForm((prev) => ({ ...prev, [field]: e.target.value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!ACCEPTED_TYPES.includes(file.type)) {
      showToast('error', t.invalidImage);
      return;
    }
    if (file.size > MAX_IMAGE_MB * 1024 * 1024) {
      showToast('error', t.imageTooBig);
      return;
    }

    setCoverFile(file);
    if (errors.coverImage) setErrors((prev) => ({ ...prev, coverImage: undefined }));

    
    const previewUrl = URL.createObjectURL(file);
    setForm((prev) => ({ ...prev, coverImage: previewUrl }));
  };

  const clearCoverImage = () => {
    setCoverFile(null);
    setForm((prev) => ({ ...prev, coverImage: '' }));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };


  const fileToBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });

  const buildPayload = async () => {
    let coverImage = form.coverImage;
    if (coverFile) {
      coverImage = await fileToBase64(coverFile);
    }
    return { ...form, coverImage, imageUrl: coverImage };
  };

  const handleSaveDraft = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = await buildPayload();
      if (isEditing) {
        const id = existingArticle._id ?? existingArticle.id;
        await updateNews(id, { ...payload, status: 'pending' });
      } else {
        await createNews({ ...payload, status: 'pending' });
      }
      showToast('success', t.savedDraft);
    } catch (err) {
      showToast('error', err?.message || 'Failed to save draft');
    } finally {
      setLoading(false);
    }
  };

  const handlePublish = async () => {
    if (!validate()) return;
    setLoading(true);
    try {
      const payload = await buildPayload();
      if (isEditing) {
        const id = existingArticle._id ?? existingArticle.id;
        await updateNews(id, payload);
        await updateNewsStatus(id, 'published');
      } else {
        const res = await createNews({ ...payload, status: 'pending' });
        const newId = res?.data?._id ?? res?.data?.id ?? res?._id ?? res?.id;
        if (newId) await updateNewsStatus(newId, 'published');
      }
      showToast('success', t.published);
      setTimeout(() => navigate('/editor/articles'), 1500);
    } catch (err) {
      showToast('error', err?.message || 'Failed to publish');
    } finally {
      setLoading(false);
    }
  };

  const inputClass = (field) =>
    `w-full rounded-2xl border px-4 py-2.5 text-sm text-char-800 placeholder-char-300 transition-colors focus:outline-none ${
      errors[field]
        ? 'border-red-300 focus:border-red-400'
        : 'border-char-200 focus:border-primary'
    }`;

  return (
    <div className="mx-auto w-full max-w-3xl space-y-6">
      {/* Toast */}
      {toast && (
        <div
          className={`fixed right-5 top-5 z-50 flex items-center gap-2.5 rounded-2xl px-4 py-3 text-sm font-bold text-white shadow-xl transition-all ${
            toast.type === 'success' ? 'bg-primary' : 'bg-red-500'
          }`}
          style={{ animation: 'fadeSlideIn 150ms ease-out' }}
        >
          {toast.type === 'success' ? <CheckCircle size={16} /> : <AlertCircle size={16} />}
          {toast.message}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-xl border border-char-200 text-char-500 transition-colors hover:bg-char-50"
        >
          <ArrowLeft size={17} />
        </button>
        <div>
          <h2 className="text-2xl font-extrabold text-char-900">
            {isEditing ? t.editTitle : t.newTitle}
          </h2>
          <p className="mt-0.5 text-sm text-char-400">{isEditing ? t.editSub : t.newSub}</p>
        </div>
      </div>

     
      {isEditing && existingArticle?.author?.name && existingArticle?.author?.role === 'reporter' && (
        <div className="flex items-center gap-3 rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3">
          <FileText size={16} className="flex-shrink-0 text-amber-500" />
          <p className="text-sm font-bold text-amber-700">
            {t.editingBanner} — {t.reporterLabel}:{' '}
            <span className="text-amber-800">{existingArticle.author.name}</span>
          </p>
        </div>
      )}

      {/* Form card */}
      <div className="rounded-3xl border border-char-100 bg-white p-6 shadow-sm space-y-5">
        {/* Title */}
        <Field label={t.titleLabel} error={errors.title}>
          <input
            type="text"
            value={form.title}
            onChange={set('title')}
            placeholder={t.titlePlaceholder}
            className={inputClass('title')}
          />
        </Field>

        {/* Sector */}
        <Field label={t.sectorLabel} error={errors.sector}>
          <select
            value={form.sector}
            onChange={set('sector')}
            className={inputClass('sector')}
          >
            <option value="">{t.sectorPlaceholder}</option>
            {SECTORS.map((s) => (
              <option key={s} value={s}>{s}</option>
            ))}
          </select>
        </Field>

        {/* Excerpt */}
        <Field label={t.excerptLabel} error={errors.excerpt}>
          <textarea
            value={form.excerpt}
            onChange={set('excerpt')}
            placeholder={t.excerptPlaceholder}
            rows={2}
            className={inputClass('excerpt')}
          />
        </Field>

        {/* Content */}
        <Field label={t.contentLabel} error={errors.content}>
          <textarea
            value={form.content}
            onChange={set('content')}
            placeholder={t.contentPlaceholder}
            rows={12}
            className={inputClass('content')}
          />
        </Field>

        {/* Cover Image: upload from computer OR paste URL */}
        <Field label={t.coverLabel} error={errors.coverImage}>
          <div className="flex flex-wrap gap-3">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/png, image/jpeg, image/webp"
              onChange={handleFileChange}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              className="flex items-center gap-2 rounded-2xl border border-dashed border-char-300 px-4 py-2.5 text-sm font-bold text-char-500 transition-colors hover:bg-char-50"
            >
              <Upload size={15} />
              {t.uploadBtn}
            </button>

            <div className="relative flex-1 min-w-[220px]">
              <ImageIcon size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-char-300" />
              <input
                type="url"
                value={coverFile ? '' : form.coverImage}
                onChange={(e) => {
                  setCoverFile(null);
                  set('coverImage')(e);
                }}
                placeholder={t.orUrl}
                disabled={!!coverFile}
                className={`${inputClass('coverImage')} pl-10 disabled:bg-char-50 disabled:text-char-300`}
              />
            </div>

            {form.coverImage && (
              <button
                type="button"
                onClick={clearCoverImage}
                className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-char-200 text-char-400 hover:bg-char-50"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {form.coverImage && (
            <img
              src={getImageUrl(form.coverImage)}
              alt="Cover preview"
              onError={(e) => { e.target.style.display = 'none'; }}
              className="mt-2 h-36 w-full rounded-2xl object-cover border border-char-100"
            />
          )}
        </Field>
      </div>

      {/* Action buttons */}
      <div className="flex flex-wrap justify-end gap-3 pb-8">
        <button
          onClick={handleSaveDraft}
          disabled={loading}
          className="flex items-center gap-2 rounded-2xl border border-char-200 px-5 py-2.5 text-sm font-extrabold text-char-600 transition-colors hover:bg-char-50 disabled:opacity-60"
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
          {t.saveDraft}
        </button>
        <button
          onClick={handlePublish}
          disabled={loading}
          className="flex items-center gap-2 rounded-2xl bg-primary px-5 py-2.5 text-sm font-extrabold text-white transition-colors hover:bg-primary-500 disabled:opacity-60"
          style={{ backgroundColor: '#16a34a' }}
        >
          {loading ? <Loader2 size={16} className="animate-spin" /> : <Send size={16} />}
          {t.publish}
        </button>
      </div>

      <style>{`
        @keyframes fadeSlideIn {
          from { opacity: 0; transform: translateY(-6px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
};

export default CreateArticle;